import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { FeatureFlagName, featureFlags } from "src/lib/featureFlags";
import type { ParsedJwt } from "src/lib/parseJwt";
import parseJwt from "src/lib/parseJwt";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import type { ProviderCredentials } from "src/state/UserCubit/CredentialsProvider";
import CredentialsProvider from "src/state/UserCubit/CredentialsProvider";

export default class LocalStorageTokenCredentialsProvider extends CredentialsProvider {
  private readonly log = (message: string, etc?: unknown): void => {
    if (featureFlags.getFlag(FeatureFlagName.loggingAuthenticationManager)) {
      // eslint-disable-next-line no-console
      console.warn(`[CRED-P-TOKEN]: ${message}`, etc ?? "");
    }
    logSentryBreadcrumb("credentialProviderToken", [message]);
  };

  currentCredentials: {
    refreshParsed?: ParsedJwt;
    accessParsed?: ParsedJwt;
    refreshToken?: string;
    accessToken?: string;
  } = {};

  async isAvailable(): Promise<boolean> {
    return true;
  }

  isVerified(): boolean {
    return true;
  }

  async isEnabled(): Promise<boolean> {
    return true;
  }

  async getCredentials(): Promise<ProviderCredentials | undefined> {
    // User selection is now handled by AuthenticationBloc
    const userId = StorageController.activeUserId;
    let refreshToken = "";
    let accessToken = "";

    if (userId) {
      refreshToken = StorageController.getItem("refreshToken") ?? "";
      accessToken = StorageController.getItem("accessToken") ?? "";
    }

    const parsedRefreshToken = parseJwt(refreshToken);
    const parsedAccessToken = parseJwt(accessToken);

    if (parsedRefreshToken.expired) {
      refreshToken = "";
      this.log("Refresh token expired, clearing from local storage");
      if (userId) {
        StorageController.removeItem("refreshToken");
      }
    }

    // Dev/test helper: ?simulateExpiredAccessToken=true forces token refresh flow
    const simulateExpired =
      new URLSearchParams(window.location.search).get(
        "simulateExpiredAccessToken"
      ) === "true";

    if (parsedAccessToken.expired || simulateExpired) {
      accessToken = "";
      this.log(
        simulateExpired
          ? "Access token simulated as expired via query param"
          : "Access token expired, clearing from local storage"
      );
      if (userId && !simulateExpired) {
        // Don't clear from storage when simulating - we want to keep the token for next reload
        StorageController.removeItem("accessToken");
      }
    }

    // check types
    if (refreshToken && parsedRefreshToken.type !== "REFRESH") {
      const error = new Error("Invalid refresh token type");
      reportErrorSentry(error);
    }

    if (accessToken && parsedAccessToken.type !== "ACCESS") {
      const error = new Error("Invalid access token type");
      reportErrorSentry(error);
    }

    this.log("Returning credentials", {
      parsedAccessToken,
      parsedRefreshToken
    });

    this.currentCredentials = {
      refreshParsed: parsedRefreshToken,
      accessParsed: parsedAccessToken,
      refreshToken,
      accessToken
    };

    return {
      refreshToken,
      accessToken
    };
  }

  async setCredentials(credentials: ProviderCredentials): Promise<void> {
    const parsedRefreshToken = parseJwt(credentials.refreshToken);
    const parsedAccessToken = parseJwt(credentials.accessToken);

    this.log("Setting credentials", {
      parsedAccessToken,
      parsedRefreshToken,
      current: this.currentCredentials
    });

    const refreshToken = credentials.refreshToken ?? "";
    const accessToken = credentials.accessToken ?? "";

    // Always persist tokens to localStorage
    // Biometric is purely for app locking, not credential storage
    if (refreshToken) {
      StorageController.setItem("refreshToken", refreshToken);
    }
    if (accessToken) {
      StorageController.setItem("accessToken", accessToken);
    }

    this.currentCredentials = {
      refreshParsed: parsedRefreshToken,
      accessParsed: parsedAccessToken,
      refreshToken: credentials.refreshToken,
      accessToken: credentials.accessToken
    };
  }

  async deleteCredentials(): Promise<void> {
    if (StorageController.activeUserId) {
      StorageController.removeItem("refreshToken");
      StorageController.removeItem("accessToken");
    }
    this.currentCredentials = {};
  }
}
